import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { Header } from "./components/Header";
import { Loader } from "./components/Loader";
import { TabBar } from "./components/TabBar";
import { AuthProvider, type AuthState } from "./lib/auth-context";
import { AuthCallback } from "./screens/AuthCallback";
import { CreateEvent } from "./screens/CreateEvent";
import { EventDetail } from "./screens/EventDetail";
import { Events } from "./screens/Events";
import { Landing } from "./screens/Landing";
import { LandingV2 } from "./screens/LandingV2";
import { Login } from "./screens/Login";
import { MapPage } from "./screens/MapPage";
import { Points } from "./screens/Points";
import { Premium } from "./screens/Premium";
import { Profile } from "./screens/Profile";
import { Register } from "./screens/Register";
import { Rewards } from "./screens/Rewards";
import { Welcome } from "./screens/Welcome";
import { WelcomeBonus } from "./screens/WelcomeBonus";
import { logout, openSession, type UserRow } from "./api/session";

// Heavy, rarely-hit screens: the scanner pulls in html5-qrcode and the ticket
// screen pulls in qrcode. Lazy-load them so they don't bloat the main bundle.
const Ticket = lazy(() => import("./screens/Ticket").then((m) => ({ default: m.Ticket })));
const ScanTickets = lazy(() => import("./screens/ScanTickets").then((m) => ({ default: m.ScanTickets })));

export function App() {
  const [auth, setAuth] = useState<AuthState>({ kind: "loading" });

  useEffect(() => {
    openSession().then((user) => {
      setAuth(user ? { kind: "user", user } : { kind: "anon" });
    });
  }, []);

  // Capture a referral from ?ref=<userId> the moment the visitor lands, before
  // they sign in. Register applies it after sign-in (before onboarding).
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref && /^[0-9a-f-]{36}$/i.test(ref)) localStorage.setItem("nifgash.ref", ref);
  }, []);

  const setUser = useCallback((user: UserRow) => {
    setAuth({ kind: "user", user });
  }, []);

  const clearUser = useCallback(() => {
    logout();
    setAuth({ kind: "anon" });
  }, []);

  if (auth.kind === "loading") return <Loader text="Подключаемся…" full />;

  return (
    <AuthProvider value={{ auth, setUser, clearUser }}>
      <BrowserRouter>
        <HeaderGate />
        <Suspense fallback={<Loader full />}>
        <Routes>
          {/* Public — no auth required */}
          {/* Landing is reachable for everyone, including signed-in users, so
              the "Preview" link in the Header actually shows it. Logged-in
              users see CTA copy aimed at exploring events, not signing in. */}
          <Route path="/" element={<LandingV2 />} />
          <Route path="/landing-original" element={<Landing />} />
          <Route path="/landing-v2" element={<Navigate to="/" replace />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/events" element={<Events />} />
          <Route path="/events/:id" element={<EventDetail />} />
          <Route
            path="/events/:id/ticket"
            element={auth.kind === "user" ? <Ticket /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/events/:id/scan"
            element={auth.kind === "user" ? <ScanTickets /> : <Navigate to="/login" replace />}
          />
          <Route path="/profile/:id" element={<Profile />} />
          {/* /login — if already authed, jump straight to /register (incomplete
              profile) or /events (full profile). Otherwise show the form. */}
          <Route
            path="/login"
            element={
              auth.kind === "user"
                ? <Navigate to={auth.user.full_name ? "/events" : "/register"} replace />
                : <Login />
            }
          />
          {/* OAuth redirect-mode landing: backend sends users here after
              Google sign-in with the JWT in the URL fragment. */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Authenticated — fall back to /login if anonymous */}
          <Route
            path="/welcome"
            element={auth.kind === "user" ? <Welcome /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/welcome-bonus"
            element={auth.kind === "user" ? <WelcomeBonus /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/register"
            element={auth.kind === "user" ? <Register /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/profile"
            element={
              auth.kind === "user"
                ? <Profile />
                : <Navigate to="/login" state={{ from: "/profile" }} replace />
            }
          />
          <Route
            path="/points"
            element={auth.kind === "user" ? <Points /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/premium"
            element={auth.kind === "user" ? <Premium /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/rewards"
            element={auth.kind === "user" ? <Rewards /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/create-event"
            element={auth.kind === "user" ? <CreateEvent /> : <Navigate to="/login" replace />}
          />

          {/* Catch-all → home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        <TabBarGate authed={auth.kind === "user"} />
      </BrowserRouter>
    </AuthProvider>
  );
}

/** Show TabBar only on Events and own Profile for signed-in users. */
function TabBarGate({ authed }: { authed: boolean }) {
  const location = useLocation();
  if (!authed) return null;
  const show = location.pathname === "/events" || location.pathname === "/profile";
  if (!show) return null;
  return <TabBar />;
}

/** Show the global Header everywhere except /login (full-screen form). */
function HeaderGate() {
  const location = useLocation();
  if (location.pathname === "/login") return null;
  return <Header />;
}
